import type {SetAttributeRequest} from './attribute-definition'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {Page} from './page'
import type {Source} from './source'

/* eslint-disable i18next/no-literal-string */
export const PartyType = {
    PERSON: 'PERSON',
    ORG_UNIT: 'ORG_UNIT',
    AGENT: 'AGENT',
} as const;
export type PartyType = typeof PartyType[keyof typeof PartyType];

export const RelationshipType = {
    PART_OF: 'PART_OF',
} as const;
export type RelationshipType = typeof RelationshipType[keyof typeof RelationshipType];

/* eslint-enable i18next/no-literal-string */

/**
 * Narrow list-payload DTO for parties. Mirrors backend {@code PartyListItem}
 * — rendered by the parties list page (party type, derivedDisplayName,
 * created-at). The detail page consumes the wider {@link Party} shape
 * (extending this interface).
 */
export interface PartyListItem {
    id: UUID;
    partyType: string;
    derivedDisplayName: string;
    createdAt: Instant;
}

/**
 * Wide detail-payload DTO for parties. Mirrors backend
 * {@code PartyDetail extends PartyListItem} — adds the fields the detail page
 * renders on top of the list shape (attributes, sources, version, updatedAt).
 *
 * {@code sources} carries the {@code party_sources} rows for this entity (Spec §6 — added in
 * 572.05). Empty when the party was created manually and is not synced from any external
 * system. The per-source identifier replaces the legacy single-valued {@code externalId} field
 * (dropped in 572.08).
 */
export interface Party extends PartyListItem {
    // eslint-disable-next-line i18next/no-literal-string
    attributes: import('./resource').EntityAttribute[] | null;
    sources: Source[];
    version: number;
    updatedAt: Instant;
}

/**
 * Party relationship response from the API.
 */
export interface PartyRelationship {
    id: UUID;
    fromParty: EntityLink;
    toParty: EntityLink;
    relationshipType: string;
    validFrom: Instant;
    validTo: Instant | null;
    version: number;
}

/**
 * Request to create a new party.
 */
export interface PartyCreateRequest {
    partyType: string;
    attributes?: SetAttributeRequest[];
}

/**
 * Request to update a party.
 */
export interface PartyUpdateRequest {
    version: number;
}

export type PartyPage = Page<Party>

/**
 * Party relationship schema config entry from the bootstrap config endpoint.
 * Display-only projection — no internal fields like schemaEntryId, maxCounts, version.
 */
export interface PartyRelationshipSchemaEntry {
    schemaEntryId: UUID;
    fromPartyType: string;
    relationshipType: string;
    toPartyType: string;
    maxFromCount: number;
    maxToCount: number;
    fromSideDisplay: boolean;
    toSideDisplay: boolean;
    fromSideTree: boolean;
    toSideTree: boolean;
    fromSideLabel: string | null;
    toSideLabel: string | null;
    iconFrom: string | null;
    iconTo: string | null;
    aliasNameFrom: string | null;
    aliasNameTo: string | null;
    sortOrder: number;
    active: boolean;
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Party relationship schema entry from the API.
 */
export interface PartyRelationshipSchema {
    schemaEntryId: UUID;
    fromPartyType: string;
    relationshipType: string;
    toPartyType: string;
    maxFromCount: number;
    maxToCount: number;
    fromSideDisplay: boolean;
    toSideDisplay: boolean;
    fromSideTree: boolean;
    toSideTree: boolean;
    iconFrom: string | null;
    iconTo: string | null;
    aliasNameFrom: string | null;
    aliasNameTo: string | null;
    fromSideLabel: string | null;
    toSideLabel: string | null;
    sortOrder: number;
    active: boolean;
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Party relationship schema violation from the API.
 */
export interface PartyRelationshipSchemaViolation {
    violationId: UUID;
    partyRelationshipId: UUID;
    fromPartyType: string;
    relationshipType: string;
    toPartyType: string;
    violationType: string;
    detail: string | null;
    createdAt: Instant;
}

export interface PartyRelationshipCreateRequest {
    fromPartyId: UUID;
    toPartyId: UUID;
    relationshipType: string;
}

export interface PartyRelationshipSchemaCreateRequest {
    fromPartyType: string;
    relationshipType: string;
    toPartyType: string;
    maxFromCount: number;
    maxToCount: number;
    fromSideDisplay: boolean;
    toSideDisplay: boolean;
    fromSideTree: boolean;
    toSideTree: boolean;
    iconFrom?: string;
    iconTo?: string;
    aliasNameFrom?: string;
    aliasNameTo?: string;
    sortOrder: number;
    fromSideLabel?: string;
    toSideLabel?: string;
}

export interface PartyRelationshipSchemaUpdateRequest {
    maxFromCount: number;
    maxToCount: number;
    fromSideDisplay: boolean;
    toSideDisplay: boolean;
    fromSideTree: boolean;
    toSideTree: boolean;
    iconFrom?: string;
    iconTo?: string;
    aliasNameFrom?: string;
    aliasNameTo?: string;
    sortOrder: number;
    active: boolean;
    fromSideLabel?: string;
    toSideLabel?: string;
    version: number;
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const PartyCreateRequestFields = {
    partyType: 'partyType',
    attributes: 'attributes',
} as const

export const PartyRelationshipResponseFields = {
    id: 'id',
    fromParty: 'fromParty',
    toParty: 'toParty',
    relationshipType: 'relationshipType',
    validFrom: 'validFrom',
    validTo: 'validTo',
    version: 'version',
} as const

export const PartyRelationshipSchemaCreateRequestFields = {
    fromPartyType: 'fromPartyType',
    relationshipType: 'relationshipType',
    toPartyType: 'toPartyType',
    maxFromCount: 'maxFromCount',
    maxToCount: 'maxToCount',
    fromSideDisplay: 'fromSideDisplay',
    toSideDisplay: 'toSideDisplay',
    fromSideTree: 'fromSideTree',
    toSideTree: 'toSideTree',
    iconFrom: 'iconFrom',
    iconTo: 'iconTo',
    aliasNameFrom: 'aliasNameFrom',
    aliasNameTo: 'aliasNameTo',
    sortOrder: 'sortOrder',
    fromSideLabel: 'fromSideLabel',
    toSideLabel: 'toSideLabel',
} as const

export const PartyRelationshipSchemaResponseFields = {
    schemaEntryId: 'schemaEntryId',
    fromPartyType: 'fromPartyType',
    relationshipType: 'relationshipType',
    toPartyType: 'toPartyType',
    maxFromCount: 'maxFromCount',
    maxToCount: 'maxToCount',
    fromSideDisplay: 'fromSideDisplay',
    toSideDisplay: 'toSideDisplay',
    fromSideTree: 'fromSideTree',
    toSideTree: 'toSideTree',
    iconFrom: 'iconFrom',
    iconTo: 'iconTo',
    aliasNameFrom: 'aliasNameFrom',
    aliasNameTo: 'aliasNameTo',
    fromSideLabel: 'fromSideLabel',
    toSideLabel: 'toSideLabel',
    sortOrder: 'sortOrder',
    active: 'active',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const PartyRelationshipSchemaUpdateRequestFields = {
    maxFromCount: 'maxFromCount',
    maxToCount: 'maxToCount',
    fromSideDisplay: 'fromSideDisplay',
    toSideDisplay: 'toSideDisplay',
    fromSideTree: 'fromSideTree',
    toSideTree: 'toSideTree',
    iconFrom: 'iconFrom',
    iconTo: 'iconTo',
    aliasNameFrom: 'aliasNameFrom',
    aliasNameTo: 'aliasNameTo',
    sortOrder: 'sortOrder',
    active: 'active',
    fromSideLabel: 'fromSideLabel',
    toSideLabel: 'toSideLabel',
    version: 'version',
} as const

export const PartyRelationshipSchemaViolationResponseFields = {
    violationId: 'violationId',
    partyRelationshipId: 'partyRelationshipId',
    fromPartyType: 'fromPartyType',
    relationshipType: 'relationshipType',
    toPartyType: 'toPartyType',
    violationType: 'violationType',
    detail: 'detail',
    createdAt: 'createdAt',
} as const

export const PartyResponseFields = {
    id: 'id',
    partyType: 'partyType',
    derivedDisplayName: 'derivedDisplayName',
    attributes: 'attributes',
    sources: 'sources',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const PartyUpdateRequestFields = {
    version: 'version',
} as const
/* eslint-enable i18next/no-literal-string */
